import { useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks';
import { useBootstrap } from '@/features/auth/hooks/useBootstrap';
import {
  countOwnedBootstrapShops,
  formatShopSlotLabel,
  getShopExpansionBlockReason,
  getShopExpansionBlockedMessage,
  resolveMaxShops,
  type ShopExpansionBlockReason,
} from '../utils/shopExpansionEntitlements';

export interface ShopExpansionEligibility {
  /** Whether the user can create another shop under their current plan */
  canAddShop: boolean;
  /** Maximum shops allowed by the plan (-1 = unlimited) */
  maxShops: number;
  /** Number of shops the user currently owns */
  currentShopCount: number;
  /** Number of shops the user currently owns */
  ownedShopCount: number;
  /** Display string e.g. "1 of 3 locations used" or "2 locations used, unlimited" */
  slotLabel: string;
  /** Why adding another shop is blocked, when blocked */
  reason: ShopExpansionBlockReason | null;
  /** Human-readable blocked reason for route alerts and disabled CTAs */
  blockedMessage: string;
  /** Whether data is still loading */
  isLoading: boolean;
}

/**
 * Computes shop expansion eligibility from subscription entitlements + bootstrap shop count.
 * Reusable across completion modal, dashboard CTA, and add-shop gating.
 */
export const useShopExpansionEligibility = (): ShopExpansionEligibility => {
  const {
    currentSubscription,
    isLoading: isAuthLoading,
    isSubscriptionLoading,
  } = useAuth();
  const { data: bootstrapData, isLoading: isBootstrapLoading } = useBootstrap();

  return useMemo(() => {
    const isLoading = isAuthLoading || isBootstrapLoading || isSubscriptionLoading;

    if (isLoading || !bootstrapData) {
      return {
        canAddShop: false,
        maxShops: 1,
        currentShopCount: 0,
        ownedShopCount: 0,
        slotLabel: '',
        reason: null,
        blockedMessage: '',
        isLoading: true,
      };
    }

    const currentShopCount = countOwnedBootstrapShops(bootstrapData.shops);
    const maxShops = resolveMaxShops(currentSubscription?.plan);
    const hasActiveSubscription = currentSubscription?.isActive === true;
    const reason = getShopExpansionBlockReason({
      currentShopCount,
      maxShops,
      hasActiveSubscription,
    });

    return {
      canAddShop: reason === null,
      maxShops,
      currentShopCount,
      ownedShopCount: currentShopCount,
      slotLabel: formatShopSlotLabel(currentShopCount, maxShops),
      reason,
      blockedMessage: getShopExpansionBlockedMessage(
        reason,
        currentSubscription?.plan?.name,
      ),
      isLoading: false,
    };
  }, [
    currentSubscription,
    bootstrapData,
    isAuthLoading,
    isBootstrapLoading,
    isSubscriptionLoading,
  ]);
};
